
<template>
    <div>
        <div v-if="aiDropdownVisible" class="ai-backdrop" @click="aiDropdownVisible = false"></div>
        <div class="top-toolbox">
            <div
                class="online-users-pill"
                v-if="filteredChatbotStoryViewActivity.length > 0"
                v-tippy="{ content: onlineUsersTooltip, placement: 'bottom' }"
            >
                <span class="online-pulse-dot"></span>
                <span class="online-count">{{ filteredChatbotStoryViewActivity.length }}</span>
                <span class="online-label">{{ $t("chatbotv2.online") }}</span>
            </div>
            <div class="toolbox-divider" v-if="filteredChatbotStoryViewActivity.length > 0"></div>
            <button class="chatbot-btn" :class="{ selected: bar === 1 }" @click="toggleBar(1)" :disabled="previewMode">
                <md-icon>bolt</md-icon>
            </button>
            <button class="chatbot-btn" :class="{ selected: bar === 6 }" @click="toggleBar(6)">
                <md-icon>electrical_services</md-icon>
            </button>
            <button class="chatbot-btn" :class="{ selected: bar === 8 }" @click="toggleBar(8)" :disabled="previewMode">
                <md-icon>image</md-icon>
            </button>
            <button class="chatbot-btn" :class="{ selected: bar === 2 }" @click="toggleBar(2)" :disabled="previewMode">
                <md-icon>search</md-icon>
            </button>
            <button 
                class="chatbot-btn" 
                :class="{ selected: bar === 9 }" 
                @click="toggleBar(9)" 
                :disabled="previewMode"
                v-tippy="{ content: $t('chatbotv2.cacheManagement') }"
            >
                <md-icon>storage</md-icon>
            </button>
            <button class="chatbot-btn" :class="{ selected: bar === 3 }" @click="toggleBar(3)" :disabled="previewMode">
                <md-icon>settings</md-icon>
            </button>
            <button class="chatbot-btn" :class="{ selected: bar === 4 }" @click="toggleBar(4)">
                <md-icon>history</md-icon>
            </button>
            <a-button type="primary" ghost @click="toggleBar(5)">
                {{ $t("chatbotv2.testBot") }}
            </a-button>
            <button class="chatbot-btn" :class="{ selected: bar === 7 }" @click="toggleBar(7)" :disabled="previewMode" v-if="debugMode">
                <img src="/static/img/copilot.png" width="16" height="16" />
            </button>
            <div class="ai-btn-container">
                <div class="ai-btn-group">
                    <button class="ai-main-btn" @click="openAIPanel('settings')">
                        <i class="material-icons ai-sparkle-icon">auto_awesome</i>
                        <span class="ai-btn-label">AI</span>
                    </button>
                    <button class="ai-chevron-btn" @click.stop="aiDropdownVisible = !aiDropdownVisible">
                        <i class="material-icons ai-chevron-icon" :class="{ rotated: aiDropdownVisible }">expand_more</i>
                    </button>
                </div>
                <transition name="ai-dropdown">
                    <div class="ai-dropdown-menu" v-if="aiDropdownVisible">
                        <div class="ai-dropdown-item" @click="openAIPanel('files')">
                            <i class="material-icons">folder_open</i>
                            <span>{{ $t("chatbotv2.knowledgeBase") }}</span>
                        </div>
                        <div class="ai-dropdown-item" @click="openAIPanel('functions')">
                            <i class="material-icons">functions</i>
                            <span>{{ $t("chatbotv2.aiFunctions") }}</span>
                        </div>
                        <div class="ai-dropdown-item" @click="openAIPanel('rag-settings')">
                            <i class="material-icons">tune</i>
                            <span>{{ $t("chatbotv2.ragSettings") }}</span>
                        </div>
                        <div class="ai-dropdown-item" @click="openAIPanel('ai-models')">
                            <i class="material-icons">psychology</i>
                            <span>{{ $t("chatbotv2.aiModels") }}</span>
                        </div>
                        <div class="ai-dropdown-item" @click="openAIPanel('plugins')">
                            <i class="material-icons">extension</i>
                            <span>{{ $t("chatbotv2.plugins") }}</span>
                        </div>
                        <div class="ai-dropdown-item" @click="openAIPanel('settings')">
                            <i class="material-icons">edit_note</i>
                            <span>{{ $t("chatbotv2.promptSettings") }}</span>
                        </div>
                    </div>
                </transition>
            </div>
            <a-button
                type="primary"
                @click="versionModal = true"
                :disabled="previewMode"
                v-if="checkPermission(PERMISSION_TYPES['chatbot.stories.update'].tr.name)"
            >
                <span class="d-flex justify-content-between align-content-center align-items-center">
                    <span class="material-icons" style="">send</span>
                    <b class="ml-2">{{ $t("chatbotv2.publish") }}</b>
                </span>
            </a-button>
        </div>

        <div class="side-bar" v-if="bar !== 0">
            <ActionPalette
                v-if="bar === 1"
                :dragMode="dragMode"
                :dragStart="dragStart"
                :dragEnd="dragEnd"
                :addBlockToDiagram="addBlockToDiagram"
                :bar="bar"
                @close-menu="bar = $event"
            />
            <SearchWidget
                :blocksMetas="blocksMetas"
                @focusBlock="focusBlock"
                v-if="!previewMode && bar === 2"
                :flattedDiagram="flattedDiagram"
                :toggleBar="toggleBar"
                :bar="bar"
                @close-menu="bar = $event"
                :board="board"
                :navigateTo="navigateTo"
            />
            <SettingsWidget
                @close-menu="bar = $event"
                v-if="!previewMode && bar === 3"
                :bar="bar"
                :diagram="diagram"
                :story-id="storyId"
                :board="board"
            />
            <VersionSwitcher :story-id="storyId" v-if="bar === 4" :bar="bar" @close-menu="bar = $event" />
            <ChatWidget
                v-if="bar === 5"
                @close-menu="bar = $event"
                :story-id="storyId"
                :version="version"
                :test-start-block-id="testStartBlockId"
                :diagram="diagram"
            />
            <AddIntergation v-if="bar === 6" :storyId="storyId" @close-menu="bar = $event" />
            <ChatbotAgent
                v-if="bar === 7 && debugMode"
                :storyId="storyId"
                :version="version"
                :bar="bar"
                @close-menu="bar = $event"
                @diagram-updated="handleDiagramUpdate"
            />
            <ImageProcessingModal 
                v-if="bar === 8" 
                :storyId="storyId" 
                @close="bar = 0"
            />
            <CacheWidget
                v-if="bar === 9"
                :story-id="storyId"
                @close-menu="bar = $event"
            />
        </div>
        <a-modal
            id="publish-modal"
            :title="$t('chatbotv2.newVersion')"
            wrapClassName="vertical-center-modal"
            v-model="versionModal"
            @ok="publishHandler"
            @cancel="versionModal = false"
            :okText="$t('message.ok')"
            :cancelText="$t('message.cancel')"
        >
            <a-form class="d-flex flex-column justify-content-center" @submit.prevent="publishHandler">
                <a-input :value="$route.params.version" placeholder="1.0" disabled="disabled" />
            </a-form>
        </a-modal>
    </div>
</template>

<script>
import ActionPalette from "../../toolbar/ActionPalette";
import SearchWidget from "../../toolbar/SearchWidget";
import SettingsWidget from "../../toolbar/SettingsWidget";
import VersionSwitcher from "../../toolbar/VersionSwitcher";
import ChatWidget from "../../preview/ChatWidget";
import ChatbotAgent from "./ChatbotAgent.vue";
import ImageProcessingModal from "./ImageProcessingModal.vue";
import CacheWidget from "./CacheWidget.vue";
import { log2 } from "../../../../../../../utils/log2";
import SYSTEM_PUBSUB from "../../../../../../../utils/systemPubSub";
import { mapActions, mapGetters, mapState } from "vuex";
import { PERMISSION_TYPES } from "../../../../../../../language/permissionTypes";
import AddIntergation from "../../toolbar/AddIntegration.vue";
import confetti from 'canvas-confetti';

const log = log2("Toolbar");

export default {
    name: "Toolbar",
    props: [
        "dragMode",
        "dragStart",
        "dragEnd",
        "addBlockToDiagram",
        "flattedDiagram",
        "blocksMetas",
        "storyId",
        "version",
        "testStartBlockId",
        "diagram",
        "defaultOpenBarIndex",
        "board",
        "previewMode",
        "navigateTo",
    ],
    components: {
        VersionSwitcher,
        SettingsWidget,
        SearchWidget,
        ActionPalette,
        ChatWidget,
        AddIntergation,
        ChatbotAgent,
        ImageProcessingModal,
        CacheWidget,
    },
    computed: {
        PERMISSION_TYPES() {
            return PERMISSION_TYPES;
        },
        ...mapGetters({
            checkPermission: "auth/checkPermission",
        }),
        usersMap() {
            var map = {};
            (this.usersList || []).forEach(function(user) {
                map[user._id] = user;
            });
            return map;
        },
        filteredChatbotStoryViewActivity() {
            var storyId = this.storyId;
            return (this.chatbotStoryViewActivity || []).filter(function(u) {
                return u.storyId === storyId && u.updateTS > Date.now() - 10000;
            });
        },
        onlineUsersTooltip() {
            var self = this;
            return this.filteredChatbotStoryViewActivity
                .filter(function(u) { return self.usersMap[u.userId]; })
                .map(function(u) { return self.usersMap[u.userId].name; })
                .join(', ');
        },
        ...mapState({
            stories: (state) => state.CB.stories,
            webChatChannel: (state) => state.channel.channels.find((channel) => channel.type === "webchat"),
            debugMode: (state) => state.app.debugMode,
            usersList: (state) => state.user.users.data || [],
            chatbotStoryViewActivity: (state) => state.app.chatbotStoryViewActivity || [],
        }),
    },
    data() {
        return {
            bar: this.previewMode ? 4 : 1,
            versionModal: false,
            aiDropdownVisible: false,
        };
    },
    methods: {
        ...mapActions({
            publishBoard: "CB/publishBoard",
            makeDefaultStory: "CB/makeDefaultStory",
            updateChannel: "channel/updateChannel",
        }),
        openAIPanel(page) {
            this.aiDropdownVisible = false;
            this.$emit('showAIKnowledge', page || 'websites');
        },
        toggleBar(index) {
            if (this.bar !== index) this.bar = index;
            else this.bar = 0;
        },
        focusBlock(block) {
            if (["START_POINT", "DEFAULT_FALLBACK"].includes(block.type)) return;
            if (block.type === "FLOW") {
                this.navigateToBlock(block);
                return;
            }
            this.$emit("openModal", block);
            let target = this.blocksMetas[`${block.id}`];
            let parentNode = this.blocksMetas[`${block.parent.id}`];
            log("focusBlock parentNode", parentNode.x, parentNode.y);
            log("focusBlock target", target.x, target.y);
            log("focusBlock block", block);
            parentNode.center = {
                x: parentNode.width / 4 - parentNode.x / 2,
                y: parentNode.height / 2 + parentNode.y / 2,
            };
            target.center = {
                x: target.x - parentNode.x + target.width / 2,
                y: target.y - parentNode.y + target.height / 2,
            };
            // log("focusBlock", target.center, parentNode.center);
            this.$emit("setTranslate", {
                diffX: parentNode.center.x - target.center.x,
                diffY: parentNode.center.y - target.center.y,
            });
        },
        runStarConfetti() {
            const defaults = {
                spread: 360,
                ticks: 50,
                gravity: 0,
                decay: 0.94,
                startVelocity: 30,
                colors: ['FFE400', 'FFBD00', 'E89400', 'FFCA6C', 'FDFFB8']
            };

            const shoot = () => {
                confetti({
                    ...defaults,
                    particleCount: 40,
                    scalar: 1.2,
                    shapes: ['star']
                });

                confetti({
                    ...defaults,
                    particleCount: 10,
                    scalar: 0.75,
                    shapes: ['circle']
                });
            };

            shoot();
            setTimeout(shoot, 100);
            setTimeout(shoot, 200);
        },
        async publishHandler() {
            await this.publishBoard({
                id: this.storyId,
                version: this.version,
                publishVersion: this.$route.params.version,
            });
            this.versionModal = false;
            this.runStarConfetti();

            const isThereAnyDefaultStory = (this.stories || []).some((story) =>
                _.get(story, "integrations.supsisWidget")
            );
            if (!isThereAnyDefaultStory) {
                const self = this;
                this.$confirm({
                    title: this.$t("chatbotv2.noDefaultStoryTitle"),
                    content: this.$t("chatbotv2.noDefaultStory"),
                    okText: this.$t("chatbotv2.yes"),
                    cancelText: this.$t("chatbotv2.no"),
                    width: 600,
                    height: 600,
                    async onOk() {
                        const webChatChannel = self.webChatChannel;
                        self.updateChannel({
                            ...webChatChannel,
                            forward: {
                                ...webChatChannel.forward,
                                mode: "chatbot",
                                story: self.storyId,
                            },
                        });
                    },
                    onCancel() {},
                });
            }
        },
        handleDiagramUpdate() {
            // Diagram güncellendiğinde parent component'a bildir
            this.$emit("diagram-updated");
        },
    },
    watch: {
        testStartBlockId(val) {
            if (val) {
                this.bar = 5;
            } else {
                if (this.bar === 5) this.bar = null;
            }
        },
        bar(val, oldVal) {
            if (val === 5) {
                SYSTEM_PUBSUB.publish(SYSTEM_PUBSUB.MESSAGE_TYPES.CB_CHAT_WIDGET_OPEN_STATE_CHANGED, {
                    visible: true,
                });
            }
            if (oldVal === 5) {
                SYSTEM_PUBSUB.publish(SYSTEM_PUBSUB.MESSAGE_TYPES.CB_CHAT_WIDGET_OPEN_STATE_CHANGED, {
                    visible: false,
                });
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.top-toolbox {
    position: absolute;
    top: 1.7rem;
    right: 2rem;
    z-index: 1002;
    display: flex;
    gap: 6px;
    background: #fff;
    padding: 3px 6px;
    border-radius: 8px;
    box-shadow: 0px 4px 20px #00000040;
    height: 50px;
    align-items: center;
}

.side-bar {
    position: absolute;
    top: 7rem;
    right: 2rem;
    z-index: 999;
    border-radius: 8px;
}

.chatbot-btn {
    border: none;
    background: #fff;
    border-radius: 4px;
    padding: 4px 8px;
    margin: 0;
    min-height: 34px;
    min-width: 34px;
    color: #000;
    display: flex;
    justify-content: center;
    align-items: center;
    user-select: none;

    &.bordered {
        border: 1px solid #000;
    }

    &.selected {
        .md-icon {
            color: #006cff !important;
        }
    }
}

.ai-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1000;
}

.online-users-pill {
    display: flex;
    align-items: center;
    gap: 5px;
    background: #f0fdf4;
    border: 1px solid #bbf7d0;
    border-radius: 20px;
    padding: 4px 10px;
    cursor: default;
    white-space: nowrap;

    .online-pulse-dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: #22c55e;
        position: relative;
        flex-shrink: 0;

        &::after {
            content: '';
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            width: 8px;
            height: 8px;
            border-radius: 50%;
            background: rgba(34, 197, 94, 0.5);
            animation: online-pulse 1.5s ease-out infinite;
        }
    }

    .online-count {
        font-size: 13px;
        font-weight: 700;
        color: #16a34a;
        line-height: 1;
    }

    .online-label {
        font-size: 11px;
        color: #16a34a;
        font-weight: 500;
        line-height: 1;
    }
}

.toolbox-divider {
    width: 1px;
    height: 24px;
    background: #e5e7eb;
    flex-shrink: 0;
}

.ai-btn-container {
    position: relative;

    .ai-btn-group {
        display: flex;
        align-items: stretch;
        border-radius: 8px;
        overflow: hidden;
        box-shadow: 0 0 15px rgba(139, 92, 246, 0.4);
    }

    .ai-main-btn {
        background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #ec4899 100%);
        background-size: 200% 200%;
        animation: ai-gradient-shift 4s ease infinite;
        border: none;
        color: #fff;
        padding: 6px 12px;
        cursor: pointer;
        display: flex;
        align-items: center;
        gap: 6px;
        font-weight: 600;
        font-size: 13px;
        letter-spacing: 0.02em;
        transition: opacity 0.2s;

        &:hover {
            opacity: 0.88;
        }

        .ai-sparkle-icon {
            font-size: 16px;
            animation: ai-sparkle-pulse 2s ease-in-out infinite;
        }
    }

    .ai-chevron-btn {
        background: linear-gradient(180deg, #8b5cf6 0%, #a855f7 100%);
        border: none;
        border-left: 1px solid rgba(255, 255, 255, 0.25);
        color: #fff;
        padding: 6px 5px;
        cursor: pointer;
        display: flex;
        align-items: center;
        transition: opacity 0.2s;

        &:hover {
            opacity: 0.85;
        }

        .ai-chevron-icon {
            font-size: 18px;
            transition: transform 0.2s ease;

            &.rotated {
                transform: rotate(180deg);
            }
        }
    }

    .ai-dropdown-menu {
        position: absolute;
        top: calc(100% + 8px);
        right: 0;
        background: #fff;
        border-radius: 12px;
        box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.06);
        overflow: hidden;
        min-width: 210px;
        z-index: 1001;
    }

    .ai-dropdown-item {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 11px 16px;
        cursor: pointer;
        transition: background 0.15s;
        font-size: 13px;
        font-weight: 500;
        color: #1f2937;

        .material-icons {
            font-size: 18px;
            color: #8b5cf6;
        }

        &:hover {
            background: linear-gradient(90deg, #f5f3ff, #fdf4ff);
            color: #7c3aed;

            .material-icons {
                color: #7c3aed;
            }
        }

        &:not(:last-child) {
            border-bottom: 1px solid #f3f4f6;
        }
    }
}

@keyframes ai-gradient-shift {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
}

@keyframes ai-sparkle-pulse {
    0%, 100% { transform: rotate(0deg) scale(1); }
    25% { transform: rotate(-15deg) scale(1.15); }
    75% { transform: rotate(15deg) scale(1.15); }
}

@keyframes online-pulse {
    0% { transform: translate(-50%, -50%) scale(1); opacity: 0.6; }
    100% { transform: translate(-50%, -50%) scale(2.5); opacity: 0; }
}

.ai-dropdown-enter-active {
    animation: ai-dropdown-appear 0.22s cubic-bezier(0.16, 1, 0.3, 1);
}

.ai-dropdown-leave-active {
    animation: ai-dropdown-disappear 0.15s ease-in forwards;
}

@keyframes ai-dropdown-appear {
    from { opacity: 0; transform: translateY(-8px) scale(0.96); }
    to { opacity: 1; transform: translateY(0) scale(1); }
}

@keyframes ai-dropdown-disappear {
    from { opacity: 1; transform: translateY(0) scale(1); }
    to { opacity: 0; transform: translateY(-8px) scale(0.96); }
}
</style>
